/**
 * React Query hooks for conversation management.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosClient } from '../lib/api.ts';

export interface ConversationSummary {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export interface ConversationMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

export interface ConversationDetail {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
  messages: ConversationMessage[];
  // ["garmin_connect"]: the coach's answers drew on Garmin data.
  ai_sources?: string[];
}

export function useConversations() {
  return useQuery<ConversationSummary[]>({
    queryKey: ['conversations'],
    queryFn: () => axiosClient.get('/conversations').then((r) => r.data),
  });
}

export function useConversation(id: number | null) {
  return useQuery<ConversationDetail>({
    queryKey: ['conversations', id],
    queryFn: () => axiosClient.get(`/conversations/${id}`).then((r) => r.data),
    enabled: id !== null,
  });
}

export function useCreateConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (title?: string) =>
      axiosClient.post('/conversations', { title }).then((r) => r.data as ConversationSummary),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useDeleteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => axiosClient.delete(`/conversations/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useAddMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, role, content }: {
      conversationId: number;
      role: 'user' | 'assistant';
      content: string;
    }) =>
      axiosClient
        .post(`/conversations/${conversationId}/messages`, { role, content })
        .then((r) => r.data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['conversations', variables.conversationId] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}
